import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Pencil, UserX, MessageCircle, Link2, Check } from "lucide-react";
import { toast } from "sonner";
import api, { fmtApiError } from "@/lib/adminApi";
import { Badge, fmtMoney, inputCls } from "./shared";
import { StudentDialog } from "./AdminStudents";
import { RecordPaymentDialog } from "./AdminPayments";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function AdminStudentDetail() {
    const { id } = useParams();
    const [student, setStudent] = useState(null);
    const [teachers, setTeachers] = useState([]);
    const [editOpen, setEditOpen] = useState(false);
    const [recordFor, setRecordFor] = useState(null);

    const load = () => api.get(`/students/${id}`).then((r) => setStudent(r.data)).catch(() => toast.error("Student not found"));
    useEffect(() => {
        load();
        api.get("/teachers").then((r) => setTeachers(r.data.items));
    }, [id]);

    if (!student) return <p className="text-sm text-muted-foreground">Loading student…</p>;

    const deactivate = async () => {
        if (!window.confirm(`Deactivate ${student.student_name}? Their records stay safe, but they will no longer get monthly fee entries.`)) return;
        await api.delete(`/students/${id}`);
        toast.success("Student deactivated");
        load();
    };

    const paymentLink = async (pid) => {
        try {
            const r = await api.post(`/payments/${pid}/payment-link`);
            await navigator.clipboard.writeText(r.data.url).catch(() => {});
            toast.success("Payment link created and copied — paste it in WhatsApp");
            load();
        } catch (e) {
            toast.error(fmtApiError(e));
        }
    };

    const remind = async (pid) => {
        try {
            const r = await api.post(`/payments/${pid}/reminder`);
            if (!r.data.phone) return toast.error("No phone number on file");
            window.open(r.data.url, "_blank");
        } catch {
            toast.error("Could not create reminder");
        }
    };

    const info = [
        ["Parent", student.parent_name], ["Email", student.parent_email], ["WhatsApp", student.phone],
        ["Country", student.country], ["Time zone", student.timezone], ["Class", student.class_level],
        ["Teacher", student.teacher_name], ["Classes / month", student.classes_per_month],
        ["Duration", student.class_duration], ["Monthly fee", student.monthly_fee ? fmtMoney(student.monthly_fee, student.currency) : null],
        ["Enrolled", student.enrollment_date],
    ];

    return (
        <div className="space-y-6" data-testid="admin-student-detail">
            <Link to="/admin/students" className="text-sm text-muted-foreground hover:text-saffron" data-testid="back-to-students">← All students</Link>

            <div className="flex flex-wrap items-start justify-between gap-4 rounded-2xl border border-border bg-white p-8 card-shadow">
                <div>
                    <div className="flex items-center gap-3">
                        <h1 className="font-serif text-3xl font-semibold text-ink" data-testid="student-detail-name">{student.student_name}</h1>
                        <Badge value={student.status} />
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground">{(student.subjects || []).join(" · ") || "No subjects"} {student.class_level ? `· ${student.class_level}` : ""}</p>
                </div>
                <div className="flex gap-3">
                    <button onClick={() => setEditOpen(true)} data-testid="edit-student-button"
                        className="flex items-center gap-2 rounded-full border border-teal/30 px-5 py-2.5 text-sm font-medium text-teal transition-colors hover:bg-teal hover:text-cream">
                        <Pencil className="h-4 w-4" /> Edit
                    </button>
                    {student.status === "active" && (
                        <button onClick={deactivate} data-testid="deactivate-student-button"
                            className="flex items-center gap-2 rounded-full border border-red-200 px-5 py-2.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50">
                            <UserX className="h-4 w-4" /> Deactivate
                        </button>
                    )}
                </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-3">
                <div className="rounded-2xl border border-border bg-white p-6 card-shadow" data-testid="student-info-card">
                    <h2 className="font-serif text-xl font-semibold text-ink">Details</h2>
                    <dl className="mt-4 space-y-3 text-sm">
                        {info.filter(([, v]) => v).map(([k, v]) => (
                            <div key={k} className="flex justify-between gap-4">
                                <dt className="text-muted-foreground">{k}</dt>
                                <dd className="text-right font-medium text-ink">{v}</dd>
                            </div>
                        ))}
                    </dl>
                    {student.notes && (
                        <p className="mt-4 rounded-xl bg-sand p-4 text-sm leading-relaxed text-muted-foreground" data-testid="student-notes">{student.notes}</p>
                    )}
                </div>

                <div className="rounded-2xl border border-border bg-white p-6 card-shadow lg:col-span-2" data-testid="student-payment-history">
                    <h2 className="font-serif text-xl font-semibold text-ink">Payment History</h2>
                    {student.payments.length === 0 ? (
                        <p className="mt-6 text-sm text-muted-foreground">
                            No fee records yet. Go to <Link to="/admin/payments" className="text-teal underline">Payments</Link> and click “Generate Monthly Fees”.
                        </p>
                    ) : (
                        <div className="mt-4 overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-border text-left text-xs uppercase tracking-[0.14em] text-muted-foreground">
                                        <th className="pb-3 pr-4 font-medium">Month</th>
                                        <th className="pb-3 pr-4 font-medium">Amount</th>
                                        <th className="pb-3 pr-4 font-medium">Status</th>
                                        <th className="pb-3 pr-4 font-medium">Method / Txn</th>
                                        <th className="pb-3 pr-4 font-medium">Paid on</th>
                                        <th className="pb-3 font-medium">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {student.payments.map((p) => (
                                        <tr key={p.id} className="border-b border-border/60 last:border-0" data-testid={`payment-row-${p.id}`}>
                                            <td className="py-3 pr-4 font-medium text-ink">{p.month_label}</td>
                                            <td className="py-3 pr-4">{fmtMoney(p.amount, p.currency)}</td>
                                            <td className="py-3 pr-4"><Badge value={p.display_status} /></td>
                                            <td className="py-3 pr-4 text-xs text-muted-foreground">
                                                {p.method || p.gateway || "—"}{p.transaction_id ? ` · ${p.transaction_id}` : ""}
                                            </td>
                                            <td className="py-3 pr-4 text-xs text-muted-foreground">{p.payment_date || "—"}</td>
                                            <td className="py-3">
                                                {p.display_status !== "paid" ? (
                                                    <div className="flex flex-wrap gap-2">
                                                        <button onClick={() => setRecordFor(p)} data-testid={`record-payment-${p.id}`}
                                                            className="flex items-center gap-1 rounded-full bg-teal px-3 py-1.5 text-xs font-medium text-cream hover:bg-teal-dark">
                                                            <Check className="h-3 w-3" /> Record
                                                        </button>
                                                        <button onClick={() => paymentLink(p.id)} data-testid={`payment-link-${p.id}`}
                                                            className="flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-ink hover:bg-sand">
                                                            <Link2 className="h-3 w-3" /> Link
                                                        </button>
                                                        <button onClick={() => remind(p.id)} data-testid={`remind-${p.id}`}
                                                            className="flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-ink hover:bg-sand">
                                                            <MessageCircle className="h-3 w-3" /> Remind
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <span className="text-xs font-medium text-teal">Settled ✓</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <StudentDialog open={editOpen} initial={student} teachers={teachers} onClose={() => setEditOpen(false)} onSaved={load} />
            <RecordPaymentDialog payment={recordFor} onClose={() => setRecordFor(null)} onSaved={() => { setRecordFor(null); load(); }} />
        </div>
    );
}
